import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue as mode,
} from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import axios from "axios"
import ReactApexChart from "react-apexcharts"

const seriesTypeOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
]

const CallStatisticsScreen = () => {
  const [statsData, setStatsData] = useState({ daily: null, weekly: null, monthly: null })
  const [view, setView] = useState("daily")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)
      setError(null)

      try {
        const { data } = await axios.get("/api/caller-id/stats")
        setStatsData({
          daily: data.daily || null,
          weekly: data.weekly || null,
          monthly: data.monthly || null,
        })
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Failed to load phone call statistics"
        setError(msg)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  const activeStats = useMemo(
    () =>
      statsData[view] || {
        buckets: [],
        totals: { totalCalls: 0, newCalls: 0, existingCalls: 0, newRatio: 0, existingRatio: 0 },
      },
    [statsData, view]
  )

  const buckets = activeStats.buckets
  const totals = activeStats.totals

  const chartConfig = useMemo(() => {
    return {
      series: [
        {
          name: "Existing Customer Calls",
          data: buckets.map((bucket) => bucket.existingCalls || 0),
        },
        {
          name: "New Calls (Number not in DB)",
          data: buckets.map((bucket) => bucket.newCalls || 0),
        },
      ],
      options: {
        chart: {
          type: "bar",
          stacked: true,
          toolbar: { show: true },
        },
        colors: ["#38A169", "#3182CE"],
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "60%",
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: buckets.map((bucket) => bucket.label),
          labels: {
            rotate: -35,
            hideOverlappingLabels: true,
          },
        },
        yaxis: {
          title: {
            text: "Calls",
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
        },
        tooltip: {
          y: {
            formatter: (value) => `${value} calls`,
          },
        },
      },
    }
  }, [buckets])

  const cardBg = mode("white", "gray.800")
  const borderColor = mode("gray.200", "gray.700")
  const tableHeaderBg = mode("gray.50", "gray.700")
  const tableRowHoverBg = mode("gray.100", "gray.600")

  return (
    <Box maxW='7xl' mx='auto' px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <VStack align='stretch' spacing={8}>
        <Box>
          <Heading size='lg' mb={2}>
            Phone Call Statistics
          </Heading>
          <Text color='gray.600' fontSize='sm'>
            Volume and mix of incoming calls, including new callers vs existing customers
          </Text>
        </Box>

        <HStack spacing={2} flexWrap='wrap'>
          {seriesTypeOptions.map((option) => (
            <Button
              key={option.value}
              size='sm'
              colorScheme='blue'
              variant={view === option.value ? "solid" : "outline"}
              onClick={() => setView(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </HStack>

        {error && (
          <Alert status='error' borderRadius='md'>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Flex gap={4} wrap='wrap'>
          <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={4} minW='220px' flex='1'>
            <Stat>
              <StatLabel>Total Calls</StatLabel>
              <StatNumber>{totals.totalCalls || 0}</StatNumber>
              <StatHelpText>{view.charAt(0).toUpperCase() + view.slice(1)} window</StatHelpText>
            </Stat>
          </Box>

          <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={4} minW='220px' flex='1'>
            <Stat>
              <StatLabel>Existing Customer Calls</StatLabel>
              <StatNumber>{totals.existingCalls || 0}</StatNumber>
              <StatHelpText>{totals.existingRatio || 0}% of total</StatHelpText>
            </Stat>
          </Box>

          <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={4} minW='220px' flex='1'>
            <Stat>
              <StatLabel>New Calls</StatLabel>
              <StatNumber>{totals.newCalls || 0}</StatNumber>
              <StatHelpText>{totals.newRatio || 0}% of total</StatHelpText>
            </Stat>
          </Box>
        </Flex>

        <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={6} shadow='md'>
          {loading ? (
            <Flex justify='center' align='center' h='320px'>
              <Spinner size='lg' />
            </Flex>
          ) : buckets.length === 0 ? (
            <Flex justify='center' align='center' h='220px'>
              <Text color='gray.500'>No call history data available</Text>
            </Flex>
          ) : (
            <ReactApexChart type='bar' series={chartConfig.series} options={chartConfig.options} height={380} />
          )}
        </Box>

        <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' shadow='md' overflowX='auto'>
          <Table variant='simple' size='sm'>
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>{view.charAt(0).toUpperCase() + view.slice(1)} Period</Th>
                <Th isNumeric>Total Calls</Th>
                <Th isNumeric>Existing</Th>
                <Th isNumeric>New</Th>
                <Th isNumeric>Existing %</Th>
                <Th isNumeric>New %</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={6} textAlign='center' py={8}>
                    <Spinner size='md' />
                  </Td>
                </Tr>
              ) : buckets.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign='center' py={8}>
                    <Text color='gray.500'>No call history data available</Text>
                  </Td>
                </Tr>
              ) : (
                buckets.map((bucket) => (
                  <Tr key={bucket.key} _hover={{ bg: tableRowHoverBg }}>
                    <Td fontWeight='medium'>{bucket.label}</Td>
                    <Td isNumeric>{bucket.totalCalls}</Td>
                    <Td isNumeric>{bucket.existingCalls}</Td>
                    <Td isNumeric>{bucket.newCalls}</Td>
                    <Td isNumeric>{bucket.existingRatio}%</Td>
                    <Td isNumeric>{bucket.newRatio}%</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  )
}

export default CallStatisticsScreen
